import Phaser from 'phaser';
import { Preferences } from '../WebPreferences';
import { musicManager } from '../audio/MusicManager';
import { getAdsRemoved, purchaseRemoveAds, getProductPrice } from '../IAPManager';
import { showBanner } from '../AdManager';
import { shouldShowRatingPrompt, markRated, markNever, openPlayStore, hasRated } from '../RatingManager';

export default class HomeScene extends Phaser.Scene {
  constructor() {
    super({ key: 'HomeScene' });
    this.scoreText          = null;
    this.bubbles            = [];
    this.puffs              = [];
    this.settingsContainer  = null;
    this.removeAdsContainer = null;
    this.ratingContainer    = null;
  }

  create(data) {
    showBanner();

    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    // ── Cotton candy background ────────────────────────────────────
    const bg = this.add.graphics();
    bg.fillGradientStyle(0xf8f0ff, 0xebe8ff, 0xf0e8ff, 0xf8f0ff, 1);
    bg.fillRect(0, 0, W, H);

    // Soft bloom orbs
    const bloom = this.add.graphics();
    [
      { x: W * 0.15, y: H * 0.20, r: W * 0.55, col: 0xff9ec5, a: 0.11 },
      { x: W * 0.88, y: H * 0.15, r: W * 0.50, col: 0x99ccff, a: 0.10 },
      { x: W * 0.50, y: H * 0.68, r: W * 0.55, col: 0xcc99ff, a: 0.09 },
      { x: W * 0.10, y: H * 0.75, r: W * 0.40, col: 0xcc99ff, a: 0.08 },
    ].forEach(({ x, y, r, col, a }) => {
      for (let i = 4; i >= 1; i--) {
        bloom.fillStyle(col, (a / 4) * i);
        bloom.fillCircle(x, y, r * (i / 4));
      }
    });

    // Scattered sparkle dots
    const dotColors = [0xffffff, 0xffcce6, 0xccddff, 0xeeccff];
    const dots = this.add.graphics();
    for (let i = 0; i < 55; i++) {
      dots.fillStyle(Phaser.Math.RND.pick(dotColors), Phaser.Math.FloatBetween(0.15, 0.55));
      dots.fillCircle(
        Phaser.Math.Between(0, W),
        Phaser.Math.Between(0, H),
        Phaser.Math.FloatBetween(1, 2.8)
      );
    }

    // ── Cotton candy corner puffs ──────────────────────────────────
    [
      { x: W * 0.04, y: H * 0.14, col: 0xcc99ff, sc: 1.0 * S },
      { x: W * 0.82, y: H * 0.10, col: 0xaad4ff, sc: 0.85 * S },
      { x: W * 0.90, y: H * 0.70, col: 0xcc99ff, sc: 0.75 * S },
      { x: W * 0.02, y: H * 0.72, col: 0xcc99ff, sc: 0.90 * S },
    ].forEach(pd => {
      const g = this.add.graphics();
      this.drawPuff(g, pd.col, pd.sc);
      g.x = pd.x; g.y = pd.y; g.alpha = 0.75;
      this.puffs.push({ gfx: g, baseX: pd.x, baseY: pd.y, t: Math.random() * Math.PI * 2 });
    });

    // ── Floating bubbles ───────────────────────────────────────────
    const bubbleColors = [0xddbbff, 0xccddff, 0xeeccff, 0xf0d6ff, 0xd6eeff];
    for (let i = 0; i < 8; i++) {
      const r      = Phaser.Math.FloatBetween(10, 22) * S;
      const x      = Phaser.Math.Between(r, W - r);
      const y      = Phaser.Math.Between(0, H);
      const speedY = Phaser.Math.FloatBetween(0.25, 0.65) * S;
      const col    = Phaser.Math.RND.pick(bubbleColors);
      const g      = this.add.graphics();
      g.x = x; g.y = y;
      this.drawBubble(g, col, r);
      this.bubbles.push({ gfx: g, speedY, r, maxX: W });
    }

    // ── Logo ───────────────────────────────────────────────────────
    const logoY = H * 0.30;

    // Glow circle
    const glow = this.add.graphics();
    glow.fillStyle(0xffffff, 0.40);
    glow.fillCircle(W / 2, logoY + 8 * sd, 105 * sd);
    glow.fillStyle(0xddbbff, 0.18);
    glow.fillCircle(W / 2, logoY + 8 * sd, 135 * sd);

    // "Food" headline
    this.add.text(W / 2, logoY - 28 * sd, 'Food', {
      fontSize:        `${72 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold italic',
      color:           '#9933cc',
      stroke:          '#f0e8ff',
      strokeThickness: 7 * dpr,
    }).setOrigin(0.5);

    // "Stack" subheading
    this.add.text(W / 2, logoY + 44 * sd, 'Stack', {
      fontSize:        `${56 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold italic',
      color:           '#aa66ff',
      stroke:          '#ddd0ff',
      strokeThickness: 6 * dpr,
    }).setOrigin(0.5);

    // Pink decorative line
    const deco = this.add.graphics();
    deco.lineStyle(3 * dpr, 0xbb88ee, 0.9);
    deco.lineBetween(W / 2 - 75 * sd, logoY + 80 * sd, W / 2 + 75 * sd, logoY + 80 * sd);

    // Gentle float tween for logo area
    this.tweens.add({
      targets: [glow],
      y: '-=5', duration: 2200, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });

    // ── Food emoji row ─────────────────────────────────────────────
    const emojis    = ['🍕', '🍔', '🌮', '🍦', '🍩', '🍪'];
    const emojiY    = H * 0.54;
    const spacing   = Math.max(W / (emojis.length + 1.5), 65 * sd);
    const startX    = W / 2 - spacing * (emojis.length - 1) / 2;

    emojis.forEach((e, i) => {
      const obj = this.add.text(startX + i * spacing, emojiY, e, {
        fontSize: `${56 * sd}px`, fontFamily: 'Arial',
      }).setOrigin(0.5);

      // Staggered bob tween
      this.tweens.add({
        targets: obj,
        y: emojiY - 6 * S,
        duration: 1400 + i * 120,
        delay:    i * 120,
        yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    });

    // ── Best score card ────────────────────────────────────────────
    const cardW  = Math.min(260 * sd, W * 0.68);
    const cardH  = 54 * sd;
    const cardY  = H * 0.64;

    const scoreCard = this.add.graphics();
    scoreCard.fillStyle(0xffffff, 0.82);
    scoreCard.fillRoundedRect(W / 2 - cardW / 2, cardY - cardH / 2, cardW, cardH, cardH / 2);
    scoreCard.lineStyle(1.5 * dpr, 0xbb88ee, 0.9);
    scoreCard.strokeRoundedRect(W / 2 - cardW / 2, cardY - cardH / 2, cardW, cardH, cardH / 2);

    this.scoreText = this.add.text(W / 2, cardY, '🏆  Best: 0 foods  |  0 height', {
      fontSize:   `${13 * sd}px`,
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontStyle:  'italic',
      color:      '#7722aa',
    }).setOrigin(0.5).setDepth(1);

    if (data && (data.highFoods !== undefined || data.highHeight !== undefined)) {
      this.scoreText.setText(`🏆  Best: ${data.highFoods || 0} foods  |  ${data.highHeight || 0} height`);
    } else {
      this.updateScores();
    }

    // ── Play + VS BOT buttons (side by side) ──────────────────────
    const totalBtnW = Math.min(320 * sd, W * 0.82);
    const btnGap    = 10 * sd;
    const btnW      = (totalBtnW - btnGap) / 2;
    const btnH      = 72 * sd;
    const btnY      = H * 0.84;
    const radius    = btnH / 2;

    const playBtnX = W / 2 - btnGap / 2 - btnW / 2;
    const botBtnX  = W / 2 + btnGap / 2 + btnW / 2;

    // ── PLAY button ───────────────────────────────────────────────
    const btnGfx = this.add.graphics();
    const drawBtn = (col) => {
      btnGfx.clear();
      btnGfx.fillStyle(col, 1);
      btnGfx.fillRoundedRect(playBtnX - btnW / 2, btnY - btnH / 2, btnW, btnH, radius);
      btnGfx.fillStyle(0xffffff, 0.22);
      btnGfx.fillRoundedRect(playBtnX - btnW / 2 + 6 * sd, btnY - btnH / 2 + 5 * sd, btnW - 12 * sd, btnH * 0.38, radius * 0.7);
    };
    drawBtn(0x9944cc);

    const hitArea = this.add.rectangle(playBtnX, btnY, btnW, btnH)
      .setInteractive({ useHandCursor: true }).setDepth(2);

    const btnText = this.add.text(playBtnX, btnY, '▶  Play', {
      fontSize:        `${24 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold',
      color:           '#ffffff',
      stroke:          '#7711aa',
      strokeThickness: 2 * dpr,
    }).setOrigin(0.5).setDepth(3);

    hitArea.on('pointerover',  () => drawBtn(0xbb77ff));
    hitArea.on('pointerout',   () => drawBtn(0x9944cc));
    hitArea.on('pointerdown',  () => {
      drawBtn(0x7733bb);
      this.tweens.add({ targets: [btnGfx, btnText], scaleX: 0.95, scaleY: 0.95, duration: 80, yoyo: true });
    });
    hitArea.on('pointerup', () => {
      drawBtn(0x9944cc);
      musicManager.start();
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.stop('HomeScene');
        this.scene.start('GameScene', { mode: 'solo' });
      });
    });

    // ── VS BOT button ─────────────────────────────────────────────
    const botGfx = this.add.graphics();
    const drawBotBtn = (col) => {
      botGfx.clear();
      botGfx.fillStyle(col, 1);
      botGfx.fillRoundedRect(botBtnX - btnW / 2, btnY - btnH / 2, btnW, btnH, radius);
      botGfx.fillStyle(0xffffff, 0.22);
      botGfx.fillRoundedRect(botBtnX - btnW / 2 + 6 * sd, btnY - btnH / 2 + 5 * sd, btnW - 12 * sd, btnH * 0.38, radius * 0.7);
    };
    drawBotBtn(0x6633bb);

    const botHitArea = this.add.rectangle(botBtnX, btnY, btnW, btnH)
      .setInteractive({ useHandCursor: true }).setDepth(2);

    const botBtnText = this.add.text(botBtnX, btnY, '🤖  VS BOT', {
      fontSize:        `${22 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold',
      color:           '#ffffff',
      stroke:          '#441199',
      strokeThickness: 2 * dpr,
    }).setOrigin(0.5).setDepth(3);

    botHitArea.on('pointerover',  () => drawBotBtn(0x9966ee));
    botHitArea.on('pointerout',   () => drawBotBtn(0x6633bb));
    botHitArea.on('pointerdown',  () => {
      drawBotBtn(0x4422aa);
      this.tweens.add({ targets: [botGfx, botBtnText], scaleX: 0.95, scaleY: 0.95, duration: 80, yoyo: true });
    });
    botHitArea.on('pointerup', () => {
      drawBotBtn(0x6633bb);
      musicManager.start();
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.stop('HomeScene');
        this.scene.start('GameScene', { mode: 'vs_bot' });
      });
    });

    // ── Leaderboard button (above play buttons, centred) ─────────
    const lbBtnW = Math.min(totalBtnW * 0.80, W * 0.60);
    const lbBtnH = 52 * sd;
    const lbBtnY = btnY - btnH * 0.5 - lbBtnH * 0.5 - 10 * sd;
    const lbBtnR = lbBtnH / 2;

    const lbGfx = this.add.graphics().setDepth(2);
    const drawLbBtn = (color) => {
      lbGfx.clear();
      lbGfx.fillStyle(color, 1);
      lbGfx.fillRoundedRect(W / 2 - lbBtnW / 2, lbBtnY - lbBtnH / 2, lbBtnW, lbBtnH, lbBtnR);
      lbGfx.fillStyle(0xffffff, 0.18);
      lbGfx.fillRoundedRect(W / 2 - lbBtnW / 2 + 6 * sd, lbBtnY - lbBtnH / 2 + 5 * sd, lbBtnW - 12 * sd, lbBtnH * 0.38, lbBtnR * 0.7);
    };
    drawLbBtn(0x5533aa);

    this.add.text(W / 2, lbBtnY, '🏆  Leaderboard', {
      fontSize:        `${18 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold',
      color:           '#ffffff',
      stroke:          '#220066',
      strokeThickness: 2 * dpr,
    }).setOrigin(0.5).setDepth(3);

    const lbHit = this.add.rectangle(W / 2, lbBtnY, lbBtnW, lbBtnH)
      .setInteractive({ useHandCursor: true }).setDepth(3);
    lbHit.on('pointerover',  () => drawLbBtn(0x7755cc));
    lbHit.on('pointerout',   () => drawLbBtn(0x5533aa));
    lbHit.on('pointerdown',  () => {
      drawLbBtn(0x3311aa);
      this.cameras.main.fadeOut(250, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.stop('HomeScene');
        this.scene.start('LeaderboardScene');
      });
    });

    // ── Remove Ads button (above leaderboard button, centred) ─────
    const removeAdsBtnY = lbBtnY - lbBtnH * 0.5 - 44 * sd * 0.5 - 8 * sd;
    const removeAdsBtnW = Math.min(totalBtnW * 0.72, W * 0.55);
    const removeAdsBtnH = 44 * sd;
    const removeAdsBtnR = removeAdsBtnH / 2;

    const removeAdsGfx = this.add.graphics().setDepth(2);
    const drawRemoveAdsBtn = (color) => {
      removeAdsGfx.clear();
      removeAdsGfx.fillStyle(color, 1);
      removeAdsGfx.fillRoundedRect(
        W / 2 - removeAdsBtnW / 2, removeAdsBtnY - removeAdsBtnH / 2,
        removeAdsBtnW, removeAdsBtnH, removeAdsBtnR
      );
    };

    const removeAdsBtnText = this.add.text(W / 2, removeAdsBtnY, '', {
      fontSize:        `${14 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold',
      color:           '#1a1a2e',
    }).setOrigin(0.5).setDepth(3);

    const removeAdsHit = this.add.rectangle(W / 2, removeAdsBtnY, removeAdsBtnW, removeAdsBtnH)
      .setDepth(3);

    const applyRemoveAdsState = (removed) => {
      if (removed) {
        drawRemoveAdsBtn(0x88cc88);
        removeAdsBtnText.setText('✓  Ads Removed');
        removeAdsHit.removeInteractive();
      } else {
        drawRemoveAdsBtn(0xddaa00);
        removeAdsBtnText.setText('⭐  Remove Ads');
        removeAdsHit.setInteractive({ useHandCursor: true });

        removeAdsHit.on('pointerover',  () => drawRemoveAdsBtn(0xffcc22));
        removeAdsHit.on('pointerout',   () => drawRemoveAdsBtn(0xddaa00));
        removeAdsHit.on('pointerdown',  () => {
          musicManager.ensureStarted();
          this.showRemoveAdsModal(sd, dpr, () => {
            // on purchase success
            drawRemoveAdsBtn(0x88cc88);
            removeAdsBtnText.setText('✓  Ads Removed');
            removeAdsHit.removeInteractive();
          });
        });
      }
    };

    getAdsRemoved().then(applyRemoveAdsState);

    // ── ⚙️ Settings gear button (right of Remove Ads row) ─────────
    const gearSize = 44 * sd;
    const gearX    = W / 2 + removeAdsBtnW / 2 + gearSize / 2 + 10 * sd;
    const gearY    = removeAdsBtnY;

    const gearGfx = this.add.graphics().setDepth(2);
    const drawGear = (color) => {
      gearGfx.clear();
      gearGfx.fillStyle(color, 0.92);
      gearGfx.fillRoundedRect(
        gearX - gearSize / 2, gearY - gearSize / 2,
        gearSize, gearSize, gearSize * 0.28
      );
    };
    drawGear(0x5533aa);

    this.add.text(gearX, gearY, '⚙️', {
      fontSize:   `${22 * sd}px`,
      fontFamily: 'Arial, sans-serif',
    }).setOrigin(0.5).setDepth(3);

    const gearHit = this.add.rectangle(gearX, gearY, gearSize, gearSize)
      .setInteractive({ useHandCursor: true }).setDepth(3);

    gearHit.on('pointerover',  () => drawGear(0x7755cc));
    gearHit.on('pointerout',   () => drawGear(0x5533aa));
    gearHit.on('pointerdown',  () => {
      drawGear(0x3311aa);
      this.showSettings(sd, dpr);
    });

    // ── ⭐ Rate Us button (below settings gear) ───────────────────
    const rateSize = gearSize;
    const rateX    = gearX;
    const rateY    = gearY + gearSize + 8 * sd;

    const rateGfx = this.add.graphics().setDepth(2);
    const drawRate = (color) => {
      rateGfx.clear();
      rateGfx.fillStyle(color, 0.92);
      rateGfx.fillRoundedRect(
        rateX - rateSize / 2, rateY - rateSize / 2,
        rateSize, rateSize, rateSize * 0.28
      );
    };
    drawRate(0xddaa00);

    const rateIcon = this.add.text(rateX, rateY, '⭐', {
      fontSize:   `${22 * sd}px`,
      fontFamily: 'Arial, sans-serif',
    }).setOrigin(0.5).setDepth(3);

    const rateHit = this.add.rectangle(rateX, rateY, rateSize, rateSize)
      .setInteractive({ useHandCursor: true }).setDepth(3);

    rateHit.on('pointerover',  () => drawRate(0xffcc22));
    rateHit.on('pointerout',   () => drawRate(0xddaa00));
    rateHit.on('pointerdown',  () => {
      drawRate(0xbb8800);
      this.showRatingBanner(sd, dpr, false);
    });

    // Hide Rate Us button if already rated
    hasRated().then(rated => {
      if (rated) {
        rateGfx.setVisible(false);
        rateIcon.setVisible(false);
        rateHit.removeInteractive();
        rateHit.setVisible(false);
      }
    });

    // ── Auto-show rating prompt if conditions met ─────────────────
    shouldShowRatingPrompt().then(show => {
      if (show) {
        this.time.delayedCall(800, () => this.showRatingBanner(sd, dpr, true));
      }
    });

    this.cameras.main.fadeIn(400, 0, 0, 0);
  }

  /* ─────────────────────── RATING BANNER ──────────────────────── */
  showRatingBanner(sd, dpr, isAutoPrompt) {
    if (this.ratingContainer) return;

    const W = this.scale.width;
    const H = this.scale.height;

    this.ratingContainer = this.add.container(0, 0).setDepth(10);

    const cardW = Math.min(300 * sd, W * 0.82);
    const gap   = 18 * sd;
    const titleY = H / 2 - 70 * sd;
    const subtitleY = titleY + 32 * sd;
    const starsY = subtitleY + 48 * sd;
    const footerY = starsY + 55 * sd;

    const cardH       = (footerY - titleY) + (isAutoPrompt ? 100 * sd : 70 * sd);
    const cardCentreY = (titleY + footerY + (isAutoPrompt ? 50 * sd : 20 * sd)) / 2;
    const cardX       = W / 2 - cardW / 2;
    const cardTop     = cardCentreY - cardH / 2;
    const cr          = 22 * sd;

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.6)
      .setInteractive();

    const shadow = this.add.graphics();
    shadow.fillStyle(0xcc99ee, 0.28);
    shadow.fillRoundedRect(cardX + 5 * sd, cardTop + 7 * sd, cardW, cardH, cr);

    const card = this.add.graphics();
    card.fillStyle(0xfcf8ff, 1);
    card.fillRoundedRect(cardX, cardTop, cardW, cardH, cr);
    card.lineStyle(2.5 * (dpr || 1), 0xbb88ee, 1);
    card.strokeRoundedRect(cardX, cardTop, cardW, cardH, cr);

    const strip = this.add.graphics();
    strip.fillStyle(0xddaa00, 1);
    strip.fillRoundedRect(cardX, cardTop, cardW, 8 * sd, { tl: cr, tr: cr, bl: 0, br: 0 });

    const title = this.add.text(W / 2, titleY, '⭐  Rate Food Stack!', {
      fontSize:        `${20 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold italic',
      color:           '#7722aa',
      stroke:          '#eeddff',
      strokeThickness: 3 * (dpr || 1),
    }).setOrigin(0.5);

    const subtitle = this.add.text(W / 2, subtitleY, 'Enjoying the game? Tap a star!', {
      fontSize:   `${13 * sd}px`,
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontStyle:  'italic',
      color:      '#5a2a7a',
    }).setOrigin(0.5);

    const allItems = [overlay, shadow, card, strip, title, subtitle];

    // ── Star rating row ──────────────────────────────────────────
    let selectedRating = 0;
    const starTexts = [];
    const starSize = 36 * sd;
    const starSpacing = starSize + 8 * sd;
    const starsStartX = W / 2 - (starSpacing * 4) / 2;

    for (let i = 1; i <= 5; i++) {
      const sx = starsStartX + (i - 1) * starSpacing;
      const starTxt = this.add.text(sx, starsY, '☆', {
        fontSize:   `${starSize}px`,
        fontFamily: 'Arial, sans-serif',
        color:      '#ccaa00',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });

      starTxt.on('pointerdown', () => {
        selectedRating = i;
        starTexts.forEach((st, idx) => {
          st.setText(idx < i ? '★' : '☆');
        });

        this.time.delayedCall(400, () => {
          if (i <= 3) {
            this.showThanksFeedback(sd, dpr);
          } else {
            markRated();
            openPlayStore();
          }
          if (this.ratingContainer) {
            this.ratingContainer.destroy(true);
            this.ratingContainer = null;
          }
        });
      });

      starTexts.push(starTxt);
      allItems.push(starTxt);
    }

    // ── Footer buttons ───────────────────────────────────────────
    if (isAutoPrompt) {
      // "Maybe Later" and "Never" buttons for auto-prompted banner
      const laterW = cardW * 0.40;
      const laterH = 38 * sd;
      const laterY = footerY + 20 * sd;
      const btnGapH = 12 * sd;

      const laterGfx = this.add.graphics();
      const drawLater = (color) => {
        laterGfx.clear();
        laterGfx.fillStyle(color, 0.9);
        laterGfx.fillRoundedRect(W / 2 - laterW - btnGapH / 2, laterY - laterH / 2, laterW, laterH, laterH / 2);
      };
      drawLater(0x7755cc);

      const laterTxt = this.add.text(W / 2 - laterW / 2 - btnGapH / 2, laterY, 'Maybe Later', {
        fontSize:   `${13 * sd}px`,
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontStyle:  'bold',
        color:      '#ffffff',
      }).setOrigin(0.5);

      const laterHit = this.add.rectangle(W / 2 - laterW / 2 - btnGapH / 2, laterY, laterW, laterH)
        .setInteractive({ useHandCursor: true });
      laterHit.on('pointerover',  () => drawLater(0x9977ee));
      laterHit.on('pointerout',   () => drawLater(0x7755cc));
      laterHit.on('pointerdown',  () => {
        if (this.ratingContainer) {
          this.ratingContainer.destroy(true);
          this.ratingContainer = null;
        }
      });

      const neverGfx = this.add.graphics();
      const drawNever = (color) => {
        neverGfx.clear();
        neverGfx.fillStyle(color, 0.85);
        neverGfx.fillRoundedRect(W / 2 + btnGapH / 2, laterY - laterH / 2, laterW, laterH, laterH / 2);
      };
      drawNever(0xccaaee);

      const neverTxt = this.add.text(W / 2 + laterW / 2 + btnGapH / 2, laterY, 'Never', {
        fontSize:   `${13 * sd}px`,
        fontFamily: 'Georgia, "Times New Roman", serif',
        color:      '#5a2a7a',
      }).setOrigin(0.5);

      const neverHit = this.add.rectangle(W / 2 + laterW / 2 + btnGapH / 2, laterY, laterW, laterH)
        .setInteractive({ useHandCursor: true });
      neverHit.on('pointerover',  () => drawNever(0xddbbff));
      neverHit.on('pointerout',   () => drawNever(0xccaaee));
      neverHit.on('pointerdown',  () => {
        markNever();
        if (this.ratingContainer) {
          this.ratingContainer.destroy(true);
          this.ratingContainer = null;
        }
      });

      allItems.push(laterGfx, laterTxt, laterHit, neverGfx, neverTxt, neverHit);
    } else {
      // "Close" button for manually triggered banner (from Rate Us button)
      const closeW = Math.min(140 * sd, cardW * 0.5);
      const closeH = 38 * sd;
      const closeY = footerY + 10 * sd;

      const closeGfx = this.add.graphics();
      const drawClose = (color) => {
        closeGfx.clear();
        closeGfx.fillStyle(color, 0.92);
        closeGfx.fillRoundedRect(W / 2 - closeW / 2, closeY - closeH / 2, closeW, closeH, closeH / 2);
      };
      drawClose(0x7755cc);

      const closeTxt = this.add.text(W / 2, closeY, 'Close', {
        fontSize:   `${13 * sd}px`,
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontStyle:  'bold',
        color:      '#ffffff',
      }).setOrigin(0.5);

      const closeHit = this.add.rectangle(W / 2, closeY, closeW, closeH)
        .setInteractive({ useHandCursor: true });
      closeHit.on('pointerover',  () => drawClose(0x9977ee));
      closeHit.on('pointerout',   () => drawClose(0x7755cc));
      closeHit.on('pointerdown',  () => {
        if (this.ratingContainer) {
          this.ratingContainer.destroy(true);
          this.ratingContainer = null;
        }
      });

      allItems.push(closeGfx, closeTxt, closeHit);
    }

    overlay.on('pointerdown', () => {
      if (this.ratingContainer) {
        this.ratingContainer.destroy(true);
        this.ratingContainer = null;
      }
    });

    this.ratingContainer.add(allItems);
    this.ratingContainer.setAlpha(0);
    this.tweens.add({
      targets: this.ratingContainer,
      alpha: 1, duration: 200, ease: 'Cubic.easeOut',
    });
  }

  /* ─────────────────── THANKS FEEDBACK TOAST ─────────────────── */
  showThanksFeedback(sd, dpr) {
    const W = this.scale.width;
    const H = this.scale.height;

    const toastW = Math.min(260 * sd, W * 0.70);
    const toastH = 60 * sd;
    const toastY = H / 2;
    const cr     = toastH / 2;

    const container = this.add.container(0, 0).setDepth(12);

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.4);

    const bg = this.add.graphics();
    bg.fillStyle(0xfcf8ff, 1);
    bg.fillRoundedRect(W / 2 - toastW / 2, toastY - toastH / 2, toastW, toastH, cr);
    bg.lineStyle(2 * (dpr || 1), 0xbb88ee, 1);
    bg.strokeRoundedRect(W / 2 - toastW / 2, toastY - toastH / 2, toastW, toastH, cr);

    const txt = this.add.text(W / 2, toastY, '💜  Thanks for your feedback!', {
      fontSize:   `${14 * sd}px`,
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontStyle:  'italic',
      color:      '#7722aa',
    }).setOrigin(0.5);

    container.add([overlay, bg, txt]);
    container.setAlpha(0);

    this.tweens.add({
      targets: container,
      alpha: 1, duration: 200, ease: 'Cubic.easeOut',
      onComplete: () => {
        this.time.delayedCall(1800, () => {
          this.tweens.add({
            targets: container,
            alpha: 0, duration: 300, ease: 'Cubic.easeIn',
            onComplete: () => container.destroy(true),
          });
        });
      },
    });
  }

  /* ─────────────────────── SETTINGS MODAL ────────────────────── */
  showSettings(sd, dpr) {
    if (this.settingsContainer) return; // already open

    const W = this.scale.width;
    const H = this.scale.height;

    this.settingsContainer = this.add.container(0, 0).setDepth(10);

    const cardW = Math.min(280 * sd, W * 0.78);
    const gap   = 52 * sd;
    const titleY = H / 2 - gap * 1.2;
    const musicY = titleY + gap;
    const sfxY   = musicY + gap * 0.85;
    const closeY = sfxY   + gap * 0.95;

    const cardH       = closeY - titleY + gap * 2.0;
    const cardCentreY = (titleY + closeY) / 2;
    const cardX       = W / 2 - cardW / 2;
    const cardTop     = cardCentreY - cardH / 2 - gap * 0.3;
    const cr          = 22 * sd;

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.6)
      .setInteractive(); // blocks clicks through to scene

    const shadow = this.add.graphics();
    shadow.fillStyle(0xcc99ee, 0.28);
    shadow.fillRoundedRect(cardX + 5 * sd, cardTop + 7 * sd, cardW, cardH, cr);

    const card = this.add.graphics();
    card.fillStyle(0xfcf8ff, 1);
    card.fillRoundedRect(cardX, cardTop, cardW, cardH, cr);
    card.lineStyle(2.5 * (dpr || 1), 0xbb88ee, 1);
    card.strokeRoundedRect(cardX, cardTop, cardW, cardH, cr);

    const strip = this.add.graphics();
    strip.fillStyle(0x9944cc, 1);
    strip.fillRoundedRect(cardX, cardTop, cardW, 8 * sd, { tl: cr, tr: cr, bl: 0, br: 0 });

    const title = this.add.text(W / 2, titleY, '⚙️  Settings', {
      fontSize:        `${22 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold italic',
      color:           '#7722aa',
      stroke:          '#eeddff',
      strokeThickness: 3 * (dpr || 1),
    }).setOrigin(0.5);

    // ── Music toggle ───────────────────────────────────────────────
    const toggleW = Math.min(220 * sd, cardW * 0.78);
    const toggleH = 42 * sd;
    const toggleR = toggleH / 2;

    const musicGfx = this.add.graphics();
    const drawMusic = (color) => {
      musicGfx.clear();
      musicGfx.fillStyle(color, 0.92);
      musicGfx.fillRoundedRect(W / 2 - toggleW / 2, musicY - toggleH / 2, toggleW, toggleH, toggleR);
    };
    drawMusic(0x5533aa);

    const musicTxt = this.add.text(W / 2, musicY,
      musicManager.isMuted() ? '🔇  Music Off' : '🔊  Music On', {
      fontSize:        `${15 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'italic',
      color:           '#ddc8ff',
    }).setOrigin(0.5);

    const musicHit = this.add.rectangle(W / 2, musicY, toggleW, toggleH)
      .setInteractive({ useHandCursor: true });
    musicHit.on('pointerover',  () => drawMusic(0x7755cc));
    musicHit.on('pointerout',   () => drawMusic(0x5533aa));
    musicHit.on('pointerdown',  () => {
      musicManager.ensureStarted();
      musicManager.setMuted(!musicManager.isMuted());
      musicTxt.setText(musicManager.isMuted() ? '🔇  Music Off' : '🔊  Music On');
      drawMusic(0x5533aa);
    });

    // ── Effects toggle ─────────────────────────────────────────────
    const sfxGfx = this.add.graphics();
    const drawSfx = (color) => {
      sfxGfx.clear();
      sfxGfx.fillStyle(color, 0.92);
      sfxGfx.fillRoundedRect(W / 2 - toggleW / 2, sfxY - toggleH / 2, toggleW, toggleH, toggleR);
    };
    drawSfx(0x5533aa);

    const sfxTxt = this.add.text(W / 2, sfxY,
      musicManager.isSfxMuted ? '🔇  Effects Off' : '🔊  Effects On', {
      fontSize:        `${15 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'italic',
      color:           '#ddc8ff',
    }).setOrigin(0.5);

    const sfxHit = this.add.rectangle(W / 2, sfxY, toggleW, toggleH)
      .setInteractive({ useHandCursor: true });
    sfxHit.on('pointerover',  () => drawSfx(0x7755cc));
    sfxHit.on('pointerout',   () => drawSfx(0x5533aa));
    sfxHit.on('pointerdown',  () => {
      musicManager.ensureStarted();
      musicManager.setSfxMuted(!musicManager.isSfxMuted);
      sfxTxt.setText(musicManager.isSfxMuted ? '🔇  Effects Off' : '🔊  Effects On');
      drawSfx(0x5533aa);
    });

    // ── Close button ───────────────────────────────────────────────
    const closeW = Math.min(140 * sd, cardW * 0.5);
    const closeH = 44 * sd;

    const closeGfx = this.add.graphics();
    const drawClose = (color) => {
      closeGfx.clear();
      closeGfx.fillStyle(color, 0.92);
      closeGfx.fillRoundedRect(W / 2 - closeW / 2, closeY - closeH / 2, closeW, closeH, closeH / 2);
    };
    drawClose(0x7755cc);

    const closeHit = this.add.rectangle(W / 2, closeY, closeW, closeH)
      .setInteractive({ useHandCursor: true });
    const closeTxt = this.add.text(W / 2, closeY, 'Close', {
      fontSize:        `${15 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold',
      color:           '#ffffff',
    }).setOrigin(0.5);

    closeHit.on('pointerover',  () => drawClose(0x9977ee));
    closeHit.on('pointerout',   () => drawClose(0x7755cc));
    closeHit.on('pointerdown',  () => {
      if (this.settingsContainer) {
        this.settingsContainer.destroy(true);
        this.settingsContainer = null;
      }
    });

    // Tap overlay to close too
    overlay.on('pointerdown', () => {
      if (this.settingsContainer) {
        this.settingsContainer.destroy(true);
        this.settingsContainer = null;
      }
    });

    this.settingsContainer.add([
      overlay, shadow, card, strip, title,
      musicGfx, musicHit, musicTxt,
      sfxGfx, sfxHit, sfxTxt,
      closeGfx, closeHit, closeTxt,
    ]);

    this.settingsContainer.setAlpha(0);
    this.tweens.add({
      targets: this.settingsContainer,
      alpha: 1, duration: 200, ease: 'Cubic.easeOut',
    });
  }

  /* ─────────────────────── REMOVE ADS MODAL ──────────────────── */
  showRemoveAdsModal(sd, dpr, onPurchaseSuccess) {
    if (this.removeAdsContainer) return; // already open

    const W = this.scale.width;
    const H = this.scale.height;

    this.removeAdsContainer = this.add.container(0, 0).setDepth(10);

    // ── Layout measurements ────────────────────────────────────────
    const cardW   = Math.min(320 * sd, W * 0.88);
    const rowH    = 36 * sd;
    const rows    = 5;
    const titleH  = 28 * sd;
    const btnH    = 52 * sd;
    const padding = 24 * sd;
    const gap     = 10 * sd;
    const cardH   = padding + titleH + gap * 1.5 + rowH * rows + gap * 2 + btnH + gap + btnH + padding * 0.8;
    const cardX   = W / 2 - cardW / 2;
    const cardY   = H / 2 - cardH / 2;
    const cr      = 22 * sd;

    // ── Overlay ────────────────────────────────────────────────────
    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.65)
      .setInteractive();

    // ── Card shadow ────────────────────────────────────────────────
    const shadow = this.add.graphics();
    shadow.fillStyle(0xcc99ee, 0.28);
    shadow.fillRoundedRect(cardX + 5 * sd, cardY + 7 * sd, cardW, cardH, cr);

    // ── Card background ────────────────────────────────────────────
    const card = this.add.graphics();
    card.fillStyle(0xfcf8ff, 1);
    card.fillRoundedRect(cardX, cardY, cardW, cardH, cr);
    card.lineStyle(2.5 * dpr, 0xbb88ee, 1);
    card.strokeRoundedRect(cardX, cardY, cardW, cardH, cr);

    // Purple top strip
    const strip = this.add.graphics();
    strip.fillStyle(0x9944cc, 1);
    strip.fillRoundedRect(cardX, cardY, cardW, 8 * sd, { tl: cr, tr: cr, bl: 0, br: 0 });

    // ── Title ──────────────────────────────────────────────────────
    const titleY = cardY + padding + titleH / 2;
    const titleTxt = this.add.text(W / 2, titleY, "⭐  What's included?", {
      fontSize:        `${18 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold italic',
      color:           '#7722aa',
      stroke:          '#eeddff',
      strokeThickness: 3 * dpr,
    }).setOrigin(0.5);

    // ── Feature rows ───────────────────────────────────────────────
    const featureItems = [
      'Remove all interstitial ads',
      'Remove all rewarded ad prompts',
      'One-time payment — yours forever',
      'Restored automatically on reinstall',
      'All rewards & tower saves — no ad required',
    ];

    const listStartY = titleY + titleH / 2 + gap * 1.5;
    const featureTexts = featureItems.map((item, i) => {
      return this.add.text(
        cardX + padding * 0.9,
        listStartY + i * (rowH + gap * 0.18),
        `✅  ${item}`,
        {
          fontSize:   `${12 * sd}px`,
          fontFamily: 'Georgia, "Times New Roman", serif',
          color:      '#3a1a5e',
          wordWrap:   { width: cardW - padding * 1.8 },
        }
      ).setOrigin(0, 0.5);
    });

    // ── Buy Now button ─────────────────────────────────────────────
    const buyBtnW = cardW - padding * 2;
    const buyBtnY = listStartY + rows * (rowH + gap * 0.18) + gap * 1.2 + btnH / 2;
    const buyBtnX = W / 2;

    const buyGfx = this.add.graphics();
    const drawBuy = (color) => {
      buyGfx.clear();
      buyGfx.fillStyle(color, 1);
      buyGfx.fillRoundedRect(buyBtnX - buyBtnW / 2, buyBtnY - btnH / 2, buyBtnW, btnH, btnH / 2);
      buyGfx.fillStyle(0xffffff, 0.20);
      buyGfx.fillRoundedRect(buyBtnX - buyBtnW / 2 + 6 * sd, buyBtnY - btnH / 2 + 5 * sd, buyBtnW - 12 * sd, btnH * 0.38, btnH * 0.38);
    };
    drawBuy(0x9944cc);

    const buyTxt = this.add.text(buyBtnX, buyBtnY, 'Buy Now', {
      fontSize:        `${16 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold',
      color:           '#ffffff',
      stroke:          '#7711aa',
      strokeThickness: 2 * dpr,
    }).setOrigin(0.5).setDepth(1);

    // Fetch localised price and update button label
    getProductPrice().then(price => {
      const label = price ? `Buy Now  —  ${price}` : 'Buy Now';
      buyTxt.setText(label);
    });

    const buyHit = this.add.rectangle(buyBtnX, buyBtnY, buyBtnW, btnH)
      .setInteractive({ useHandCursor: true }).setDepth(2);

    buyHit.on('pointerover',  () => drawBuy(0xbb77ff));
    buyHit.on('pointerout',   () => drawBuy(0x9944cc));
    buyHit.on('pointerdown',  () => {
      drawBuy(0x7733bb);
      buyHit.removeInteractive();
      buyTxt.setText('Processing...');

      purchaseRemoveAds(
        () => {
          if (this.removeAdsContainer) {
            this.removeAdsContainer.destroy(true);
            this.removeAdsContainer = null;
          }
          onPurchaseSuccess();
        },
        () => {
          drawBuy(0x9944cc);
          buyTxt.setText('Buy Now');
          getProductPrice().then(price => {
            if (price) buyTxt.setText(`Buy Now  —  ${price}`);
          });
          buyHit.setInteractive({ useHandCursor: true });
        }
      );
    });

    // ── No Thanks button ───────────────────────────────────────────
    const noThanksY = buyBtnY + btnH / 2 + gap + btnH * 0.45;
    const noThanksW = buyBtnW * 0.55;
    const noThanksH = btnH * 0.72;

    const noThanksGfx = this.add.graphics();
    const drawNoThanks = (color) => {
      noThanksGfx.clear();
      noThanksGfx.fillStyle(color, 0.85);
      noThanksGfx.fillRoundedRect(W / 2 - noThanksW / 2, noThanksY - noThanksH / 2, noThanksW, noThanksH, noThanksH / 2);
    };
    drawNoThanks(0xccaaee);

    const noThanksTxt = this.add.text(W / 2, noThanksY, 'No Thanks', {
      fontSize:   `${13 * sd}px`,
      fontFamily: 'Georgia, "Times New Roman", serif',
      color:      '#5a2a7a',
    }).setOrigin(0.5).setDepth(1);

    const noThanksHit = this.add.rectangle(W / 2, noThanksY, noThanksW, noThanksH)
      .setInteractive({ useHandCursor: true }).setDepth(2);

    const closeModal = () => {
      if (this.removeAdsContainer) {
        this.removeAdsContainer.destroy(true);
        this.removeAdsContainer = null;
      }
    };

    noThanksHit.on('pointerover',  () => drawNoThanks(0xddbbff));
    noThanksHit.on('pointerout',   () => drawNoThanks(0xccaaee));
    noThanksHit.on('pointerdown',  closeModal);
    overlay.on('pointerdown', closeModal);

    this.removeAdsContainer.add([
      overlay, shadow, card, strip, titleTxt,
      ...featureTexts,
      buyGfx, buyTxt, buyHit,
      noThanksGfx, noThanksTxt, noThanksHit,
    ]);

    this.removeAdsContainer.setAlpha(0);
    this.tweens.add({
      targets: this.removeAdsContainer,
      alpha: 1, duration: 200, ease: 'Cubic.easeOut',
    });
  }

  update() {
    // Bubble rise
    this.bubbles.forEach(b => {
      b.gfx.y -= b.speedY;
      if (b.gfx.y < -b.r * 2) {
        b.gfx.y = this.scale.height + b.r * 2;
        b.gfx.x = Phaser.Math.Between(b.r, b.maxX - b.r);
      }
    });

    // Puff drift
    this.puffs.forEach(p => {
      p.t += 0.007;
      p.gfx.x = p.baseX + Math.sin(p.t) * 6;
      p.gfx.y = p.baseY + Math.cos(p.t * 0.7) * 4;
    });
  }

  updateScores() {
    Promise.all([
      Preferences.get({ key: 'highFoods' }),
      Preferences.get({ key: 'highHeight' }),
    ]).then(([a, h]) => {
      const highFoods  = parseInt(a.value) || 0;
      const highHeight = parseInt(h.value) || 0;
      if (this.scoreText) {
        this.scoreText.setText(`🏆  Best: ${highFoods} foods  |  ${highHeight} height`);
      }
    });
  }

  drawPuff(gfx, col, scale) {
    gfx.fillStyle(col, 0.28);
    [
      { ox:   0, oy:  0, r: 32 }, { ox:  30, oy: -8, r: 26 },
      { ox: -30, oy: -6, r: 24 }, { ox:  14, oy:-22, r: 20 },
      { ox: -14, oy:-20, r: 19 }, { ox:  46, oy:  5, r: 18 },
      { ox: -46, oy:  5, r: 17 }, { ox:   0, oy: 18, r: 22 },
      { ox:  24, oy: 14, r: 15 },
    ].forEach(b => gfx.fillCircle(b.ox * scale, b.oy * scale, b.r * scale));
    gfx.fillStyle(0xffffff, 0.20);
    gfx.fillCircle(-6 * scale, -12 * scale, 18 * scale);
  }

  drawBubble(gfx, col, r) {
    gfx.fillStyle(col, 0.10);
    gfx.fillCircle(0, 0, r * 2.0);
    gfx.fillStyle(col, 0.22);
    gfx.fillCircle(0, 0, r);
    gfx.lineStyle(1.2, 0xffffff, 0.55);
    gfx.strokeCircle(0, 0, r);
    gfx.fillStyle(0xffffff, 0.55);
    gfx.fillCircle(-r * 0.30, -r * 0.32, r * 0.22);
    gfx.fillStyle(0xffffff, 0.35);
    gfx.fillCircle(r * 0.18, -r * 0.48, r * 0.10);
  }
}
